import React, { useEffect, useRef, forwardRef, useState } from "react"
import DatePicker from "react-datepicker"
import Svg from "../_svgs/svg"
import { isMobile } from "react-device-detect"

const SearchOverlay = (props) => {
  const {
    searchDate,
    minDate,
    minTime,
    maxTime,
    locale,
    handleDateChange,
    showTime,
    datepickerOpen,
    handleDatepicker,
    closeDatepicker,
    handleDirectionChange,
    direction,
    departuresTabLabel,
    arrivalsTabLabel,
    q,
    flights,
    minCharsBeforeSearch,
    searchHelperText,
    numberOfFlights,
    numberOfFlightsMobile,
    departuresPage,
    arrivalsPage,
    flightClickHandler,
    noResultsLabel,
    seeMoreFlightsDepartures,
    seeMoreFlightsArrivals,
    airports,
    airlines,
    allFlightsTo,
    allFlightsFrom,
    allFlightsLimit,
    carriersLimit,
    allFlightsWithCarrier,
    timeLabel,
    departuresTabIcon,
    arrivalsTabIcon,
    showOverlay,
  } = props

  const [openedDatePicker, setOpenedDatePicker] = useState(false)
  const departuresTabRef = useRef(null)
  const arrivalsTabRef = useRef(null)
  const [activeTabIndex, setActiveTabIndex] = useState(direction === "D" ? 0 : 1)

  const calendarRef = useRef(null)

  useEffect(() => {
    const setVh = () => {
      const vhCalc = window.innerHeight * 0.01
      document.documentElement.style.setProperty("--vh", `${vhCalc}px`)
    }

    setVh()
    window.addEventListener("resize", setVh)

    return () => {
      window.removeEventListener("resize", setVh)
    }
  }, [])

  const toggleCalendarOpen = () => {
    setOpenedDatePicker(!openedDatePicker)
    if (calendarRef.current && calendarRef.current.setOpen) {
      const isOpen = calendarRef.current.state.open
      calendarRef.current.setOpen(!isOpen)
      if (calendarRef && calendarRef.current && isOpen && datepickerOpen) calendarRef.current.setFocus()
    }
    handleDatepicker()
  }

  const handleOutsideClick = () => {
    if (calendarRef.current && calendarRef.current.setOpen) {
      calendarRef.current.setOpen(false)
    }
    setOpenedDatePicker(false)
    closeDatepicker()
  }

  const handleTabKeyNavigation = (event) => {
    if (event.key === "ArrowRight") {
      if (activeTabIndex === 0) {
        setActiveTabIndex(1)
        arrivalsTabRef.current?.focus()
        props.handleDirectionChange("A")
      }
    } else if (event.key === "ArrowLeft") {
      if (activeTabIndex === 1) {
        setActiveTabIndex(0)
        departuresTabRef.current?.focus()
        props.handleDirectionChange("D")
      }
    }
  }

  const handleTabClick = (index, direction) => {
    setActiveTabIndex(index)
    props.handleDirectionChange(direction)
  }

  const shortDateOptions = { month: "short", day: "2-digit" }
  const shortSearchDate = new Date(searchDate).toLocaleDateString(locale.code, shortDateOptions)
  const resolvedNoResultsLabel =
    noResultsLabel?.replace("{input}", q).replace("{date}", shortSearchDate) || `Ingen resultater matchede din søgning på "${q}"`

  const renderSearchResults = q && q.length >= (minCharsBeforeSearch ?? 2)
  const isOpen = q && q.length > 0

  const formattedDate = searchDate.toFormat("dd / MM / yyyy")
  const formattedTime = searchDate.toFormat("HH")

  const departuresTabIconColor = direction === "D" ? "#4E55AB" : "#060e4d"
  const arrivalsTabIconColor = direction === "A" ? "#4E55AB" : "#060e4d"

  const departuresIcon = <Svg isDecorative={true} iconName={departuresTabIcon || "planetakeoff"} fillColor="none" stroke={departuresTabIconColor} />
  const arrivalsIcon = <Svg isDecorative={true} iconName={arrivalsTabIcon || "planelanding"} fillColor="none" stroke={arrivalsTabIconColor} />

  const activeTabindex = showOverlay ? 0 : -1

  const CustomInput = forwardRef(({ value }, ref) => (
    <div
      id="flight-datepicker"
      aria-pressed={openedDatePicker}
      role="button"
      tabIndex={activeTabindex}
      className="react-datepicker-ignore-onclickoutside search-datepicker"
      onClick={toggleCalendarOpen}
      onMouseDown={toggleCalendarOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === "13") toggleCalendarOpen()
      }}
      ref={ref}
    >
      {value}
    </div>
  ))
  CustomInput.displayName = "CustomInput"

  return (
    <div aria-hidden={showOverlay ? "false" : "true"} className="form__section__inner">
      <div
        className={`search-overlay ${!isMobile && isOpen && !renderSearchResults ? "is-open" : ""} ${
          !isMobile && renderSearchResults ? "has-results" : !isMobile && !renderSearchResults ? "is-closed" : ""
        } ${showOverlay ? "" : "hide-overlay"} ${isMobile && showOverlay ? "mobile-open" : ""}`}
      >
        {!isMobile && q.length < (minCharsBeforeSearch ?? 2) ? (
          <div className="form__section__inner">
            <div className="search-results helper-text">
              <div className="flights__table stylish-table">
                <div className="stylish-table__row stylish-table__row--body">
                  <p>{searchHelperText || `Du skal minimum skrive ${minCharsBeforeSearch ?? 2} karakterer for at få resultater.`}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div role="tablist" className={`tabs ${isOpen && !renderSearchResults ? "is-round" : ""}`}>
              <div className={`dateselect ${datepickerOpen ? "datepicker-open" : ""} ${!isMobile ? "is-desktop" : ""}`}>
                <DatePicker
                  ref={calendarRef}
                  selected={searchDate.toJSDate()}
                  onChange={(date) => handleDateChange(date)}
                  showTimeSelect={showTime || false}
                  dateFormat="dd. MMM yyyy"
                  minDate={minDate.toJSDate()}
                  minTime={minTime.toJSDate()}
                  maxTime={maxTime.toJSDate()}
                  locale={locale}
                  customInput={<CustomInput />}
                  timeCaption={timeLabel || "Tidspunkt"}
                  timeIntervals={60}
                  onClickOutside={handleOutsideClick}
                  onCalendarClose={closeDatepicker}
                  chooseDayAriaLabelPrefix={props.locale === "da" ? "Vælg" : "Choose"}
                />
              </div>
              <button
                role="tab"
                id={`${departuresTabLabel}-tab`}
                aria-selected={direction === "D" ? "true" : "false"}
                aria-controls={`${departuresTabLabel}-tabdata`}
                className={`button button--medium has-icon no-shadow ${direction === "D" ? "is-active" : ""}`}
                onClick={() => handleTabClick(0, "D")}
                onKeyDown={handleTabKeyNavigation}
                ref={departuresTabRef}
              >
                {departuresIcon}
                <span className="text">{departuresTabLabel || "Afgange"}</span>
              </button>
              <button
                id={`${arrivalsTabLabel}-tab`}
                role="tab"
                aria-selected={direction === "A" ? "true" : "false"}
                aria-controls={`${arrivalsTabLabel}-tabdata`}
                className={`button button--medium has-icon no-shadow ${direction === "A" ? "is-active" : ""}`}
                onClick={() => handleTabClick(1, "A")}
                onKeyDown={handleTabKeyNavigation}
                ref={arrivalsTabRef}
              >
                {arrivalsIcon}
                <span className="text">{arrivalsTabLabel || "Ankomster"}</span>
              </button>

              <div className="clear"></div>
            </div>

            {(isMobile && renderSearchResults) || !isMobile ? (
              <div
                role="tabpanel"
                id={`${direction === "D" ? `${departuresTabLabel}-tabdata}` : `${arrivalsTabLabel}-tabdata`}`}
                aria-labelledby={`${direction === "D" ? `${departuresTabLabel}-tab` : `${arrivalsTabLabel}-tab`}`}
                className={`search-results ${isMobile && datepickerOpen ? "datepicker-open" : ""} ${isMobile && showOverlay ? "make-sticky" : ""}`}
              >
                <div className="flights__table stylish-table">
                  {flights && flights.length > 0 ? (
                    flights.map((flight, index) => {
                      const currentUrl = direction === "D" ? departuresPage : arrivalsPage
                      const limit = isMobile ? numberOfFlightsMobile || 10 : numberOfFlights || 5

                      if (index < limit) {
                        const rowContent = (
                          <>
                            <div className="stylish-table__cell flights__table__col--time">
                              <div>
                                {flight.Delayed ? (
                                  <>
                                    <span className="t--strike">{`${flight.Time}`}</span>
                                    <span>
                                      <em>{`${flight.ExpectedTime}`}</em>
                                    </span>
                                  </>
                                ) : (
                                  <span>
                                    <em>{`${flight.Time}`}</em>
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="stylish-table__cell flights__table__col--destination">
                              <div>
                                <span className="v--desktop-only">
                                  <strong>
                                    <span>{flight.Destination}</span>
                                  </strong>
                                </span>
                                <span className="v--mobile-only">
                                  <strong>{flight.Destination}</strong> ({flight.IataDestination})
                                </span>
                                <span className="v--mobile-only">{flight.AirlineMobile ? flight.AirlineMobile : flight.Airline}</span>
                              </div>
                            </div>
                            <div className="stylish-table__cell flights__table__col--iataDestination v--desktop-only">
                              <div>
                                <span>{flight.IataDestination}</span>
                              </div>
                            </div>
                            <div className="stylish-table__cell flights__table__col--carrier v--desktop-only">
                              <div>
                                <span>
                                  <span>{flight.Airline}</span>
                                </span>
                              </div>
                            </div>
                            <div className="stylish-table__cell flights__table__col--gate">
                              <div>
                                <span>{flight.Iata}</span>
                              </div>
                            </div>
                          </>
                        )

                        return flightClickHandler ? (
                          <a
                            tabIndex={activeTabindex}
                            key={index}
                            className="stylish-table__row stylish-table__row--body pointer"
                            onClick={(e) => flightClickHandler(e, flight)}
                          >
                            {rowContent}
                          </a>
                        ) : (
                          <a
                            tabIndex={activeTabindex}
                            key={index}
                            className="stylish-table__row stylish-table__row--body pointer"
                            href={`${currentUrl}/${flight.Iata}?id=${flight.FlightId}`}
                          >
                            {rowContent}
                          </a>
                        )
                      }
                      return null
                    })
                  ) : (
                    <div className="stylish-table__row stylish-table__row--body no-results">{resolvedNoResultsLabel}</div>
                  )}
                </div>

                {flights && flights.length > 0 && (
                  <div className="see-more-link">
                    {(direction === "D" && seeMoreFlightsDepartures) || (direction === "A" && seeMoreFlightsArrivals) ? (
                      <a
                        tabIndex={activeTabindex}
                        href={`${direction === "D" ? departuresPage : arrivalsPage}?q=${q}&date=${formattedDate}&time=${formattedTime}`}
                      >
                        <Svg isDecorative={true} iconName="arrowright-slim" fillColor="none" />
                        <span className="link-text">
                          {direction === "D" ? seeMoreFlightsDepartures.replace("{input}", q) : seeMoreFlightsArrivals.replace("{input}", q)}
                        </span>
                      </a>
                    ) : null}
                  </div>
                )}

                {airports && airports.length > 0 && (
                  <div className="all-flights">
                    <h3>{direction === "D" ? allFlightsTo : allFlightsFrom}</h3>
                    {airports.map((airport, index) => {
                      if (index >= allFlightsLimit) return null
                      return (
                        <a
                          tabIndex={activeTabindex}
                          key={airport.Iata + index}
                          href={`${direction === "D" ? departuresPage : arrivalsPage}?q=${airport.Iata}&date=${formattedDate}&time=${formattedTime}`}
                        >
                          {`${airport.Name} (${airport.Iata})`}
                        </a>
                      )
                    })}
                  </div>
                )}

                {airlines && airlines.length > 0 && (
                  <div className="all-flights">
                    <h3>{allFlightsWithCarrier}</h3>
                    {airlines.map((airline, index) => {
                      if (index >= carriersLimit) return null
                      return (
                        <a
                          tabIndex={activeTabindex}
                          key={airline.iata + index}
                          href={`${direction === "D" ? departuresPage : arrivalsPage}?q=${airline.iata}&date=${formattedDate}&time=${formattedTime}`}
                        >
                          {`${isMobile && airline.nameMobile ? airline.nameMobile : airline.name} (${airline.iata})`}
                        </a>
                      )
                    })}
                  </div>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

export default SearchOverlay
